import React, { useState, useEffect } from 'react';
import { localToday, dateStr } from '../utils/dates';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import app, { db } from '../firebase';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Chip, Divider, Grid,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SendIcon from '@mui/icons-material/Send';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const STATIC_TARGETS = [
  { value: 'all',     label: 'All Users' },
  { value: 'coaches', label: 'Coaches Only' },
  { value: 'parents', label: 'Parents & Athletes Only' },
];

const TEMPLATES = [
  { label: 'Class Reminder',   title: 'Class Tomorrow 🏐',      body: 'Reminder: You have a class scheduled for tomorrow. See you on the court!' },
  { label: 'Cancellation',     title: 'Class Cancelled ⚠️',     body: 'A class has been cancelled. Please check the schedule for updates.' },
  { label: 'Announcement',     title: 'Torch Beach VB 📣',       body: '' },
  { label: 'Payment Reminder', title: 'Session Balance Low 💳',  body: 'Your session balance is running low. Purchase more sessions to keep training!' },
  { label: 'New Class Added',  title: 'New Class Available 🆕',  body: 'A new class has been added to the schedule. Check it out!' },
  { label: 'Tournament Reminder', title: 'Tournament Coming Up 🏆', body: "Don't forget — your tournament is coming up! Check in early and bring your gear." },
  { label: 'Tournament Canceled', title: 'Tournament Canceled 🚫', body: 'This tournament has been canceled and all payments will be refunded.' },
];

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m-1]} ${+d}, ${y}`;
}

function Notifications() {
  const [target, setTarget] = useState('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    // Load future/current tournaments + the most recent completed tournament
    async function loadTournaments() {
      try {
        const today = localToday();
        const snap = await getDocs(query(collection(db, 'tournaments'), orderBy('date', 'asc')));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const upcoming = all.filter(t => (t.date || '') >= today);
        // Find the most recent past tournament (highest date < today)
        const past = all
          .filter(t => (t.date || '') < today)
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        const lastCompleted = past[0];
        // Show past first (most recent completed at top), then upcoming
        const list = lastCompleted ? [{ ...lastCompleted, _isPast: true }, ...upcoming] : upcoming;
        setTournaments(list);
      } catch(e) { console.error('Failed to load tournaments:', e); logError('Notifications', e.message || String(e)); }
    }
    loadTournaments();
  }, []);

  // All targets: static + tournament entries
  const allTargets = [
    ...STATIC_TARGETS,
    ...(tournaments.length > 0 ? [{ value: '__divider__', label: '── Tournaments ──', disabled: true }] : []),
    ...tournaments.map(t => ({
      value: `tournament_${t.id}`,
      label: `${t.name} (${fmtDate(t.date)})${t._isPast ? ' — completed' : ''}`,
      isTournament: true,
      isPast: !!t._isPast,
      tournamentId: t.id,
      tournamentName: t.name,
    })),
  ];

  const selectedTarget = allTargets.find(t => t.value === target);

  const applyTemplate = (tpl) => { setTitle(tpl.title); setBody(tpl.body); };

  const handleSend = async () => {
    if (!title.trim()) { setError('Please enter a title.'); return; }
    if (!body.trim())  { setError('Please enter a message.'); return; }
    setSending(true);
    setError('');
    try {
      const functions = getFunctions(app, 'us-central1');
      const sendNotification = httpsCallable(functions, 'sendPushNotification');
      const result = await sendNotification({
        target,
        title: title.trim(),
        body: body.trim(),
      });
      const sent = result.data?.sent || 0;
      setSuccess(`Notification sent to ${sent} device${sent !== 1 ? 's' : ''}.`);
      setHistory(prev => [{
        target,
        targetLabel: selectedTarget?.label || target,
        title, body, sent,
        sentAt: new Date().toLocaleString(),
      }, ...prev.slice(0, 9)]);
      setTitle(''); setBody('');
      setTimeout(() => setSuccess(''), 5000);
    } catch(err) {
      console.error('Send failed:', err);
      logError('Notifications', err.message || String(err), err.stack || '');
      setError(`Failed: ${err.message || err.code || JSON.stringify(err)}`);
    }
    setSending(false);
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
            Push Notifications
          </Typography>
          <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
            Send push notifications to users who have enabled alerts.
          </Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error   && <Alert severity="error"   sx={{ mb: 3 }}>{error}</Alert>}

        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
            Compose Notification
          </Typography>

          {/* Templates */}
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>Quick Templates</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {TEMPLATES.map(tpl => (
              <Chip key={tpl.label} label={tpl.label} onClick={() => applyTemplate(tpl)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#DEEAF1' } }} />
            ))}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth select label="Send To" value={target}
                         onChange={e => { if (e.target.value !== '__divider__') setTarget(e.target.value); }}>
                {allTargets.map(t => (
                  <MenuItem key={t.value} value={t.value} disabled={t.disabled || false}
                            sx={t.isTournament ? { pl: 3 } : t.disabled ? { opacity: 0.5, fontSize: 12 } : {}}>
                    {t.isTournament && <EmojiEventsIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />}
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              {selectedTarget?.isTournament && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Sends to all registered players with FCM tokens for this tournament.
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField fullWidth label="Title *" value={title}
                         onChange={e => setTitle(e.target.value)}
                         placeholder="e.g. Tournament Reminder 🏆"
                         inputProps={{ maxLength: 60 }}
                         helperText={`${title.length}/60`} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={3} label="Message *" value={body}
                         onChange={e => setBody(e.target.value)}
                         placeholder="Enter your message here..."
                         inputProps={{ maxLength: 200 }}
                         helperText={`${body.length}/200`} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained"
                    startIcon={sending ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon />}
                    onClick={handleSend} disabled={sending || !title || !body}
                    sx={{ backgroundColor: '#1E3A5F', px: 4, '&:hover': { backgroundColor: '#2E6DA4' } }}>
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </Box>
        </Paper>

        {/* Send history */}
        {history.length > 0 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
              Recent Sends
            </Typography>
            {history.map((h, i) => (
              <Box key={i} sx={{ py: 1.5, borderBottom: i < history.length - 1 ? '1px solid #eee' : 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>{h.body}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                    <Chip label={h.targetLabel} size="small"
                          sx={{ mb: 0.5, backgroundColor: '#EBF5FB', color: '#1A5276' }} />
                    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                      {h.sent} sent · {h.sentAt}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Layout>
  );
}

export default Notifications;
